import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Edit, Trash2, Upload, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const BulletinsTab = () => {
  const [bulletins, setBulletins] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingBulletin, setEditingBulletin] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    media_url: '',
    media_type: ''
  });

  useEffect(() => {
    fetchBulletins();
  }, []);

  const fetchBulletins = async () => {
    try {
      const { data } = await axios.get(`${API}/api/bulletins/`);
      setBulletins(data);
    } catch (error) {
      console.error('Failed to fetch bulletins:', error);
    }
  };

  const handleMediaUpload = async (file) => {
    if (!file) return;
    
    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const token = localStorage.getItem('access_token');
      const { data } = await axios.post(
        `${API}/api/admin/upload/bulletin-media`,
        formDataUpload,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          },
          withCredentials: true
        }
      );
      
      setFormData(prev => ({
        ...prev,
        media_url: data.url,
        media_type: data.media_type
      }));
      
      alert(`${data.media_type === 'photo' ? 'Photo' : 'Video'} uploaded successfully!`);
    } catch (error) {
      alert(`Upload failed: ${error.response?.data?.detail || error.message}`);
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('access_token');
    const authConfig = {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      withCredentials: true
    };
    
    try {
      if (editingBulletin) {
        // Update existing
        await axios.put(
          `${API}/api/bulletins/${editingBulletin.bulletin_id}`,
          formData,
          authConfig
        );
        alert('Bulletin updated successfully!');
      } else {
        // Create new
        await axios.post(
          `${API}/api/bulletins/`,
          formData,
          authConfig
        );
        alert('Bulletin created successfully!');
      }
      
      setFormData({ title: '', description: '', media_url: '', media_type: '' });
      setEditingBulletin(null);
      setShowForm(false);
      fetchBulletins();
    } catch (error) {
      alert(`Failed to save bulletin: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleEdit = (bulletin) => {
    setEditingBulletin(bulletin);
    setFormData({
      title: bulletin.title,
      description: bulletin.description,
      media_url: bulletin.media_url || '',
      media_type: bulletin.media_type || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (bulletinId) => {
    if (!window.confirm('Are you sure you want to delete this bulletin?')) return;
    
    try {
      const token = localStorage.getItem('access_token');
      await axios.delete(`${API}/api/bulletins/${bulletinId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true
      });
      alert('Bulletin deleted successfully!');
      fetchBulletins();
    } catch (error) {
      alert(`Failed to delete bulletin: ${error.response?.data?.detail || error.message}`);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="font-['Bebas_Neue'] text-2xl">BULLETIN BOARD MANAGEMENT</h2>
        <button
          onClick={() => {
            setShowForm(!showForm);
            setEditingBulletin(null);
            setFormData({ title: '', description: '', media_url: '', media_type: '' });
          }}
          className="flex items-center gap-2 bg-[#D4AF37] text-black px-6 py-3 hover:bg-[#E5C048] transition"
        >
          {showForm ? <X size={20} /> : <Plus size={20} />}
          {showForm ? 'Cancel' : 'New Bulletin'}
        </button>
      </div>

      {/* Create/Edit Form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="bg-[#1A1A1A] p-6 rounded-lg space-y-4">
          <h3 className="text-xl text-white mb-4">
            {editingBulletin ? 'Edit Bulletin' : 'Create New Bulletin'}
          </h3>
          
          <div>
            <label className="block text-sm text-[#A3A3A3] mb-2">Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full bg-[#0D0D0D] text-white p-3 border border-[#333]"
              required
            />
          </div>

          <div>
            <label className="block text-sm text-[#A3A3A3] mb-2">Description *</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full bg-[#0D0D0D] text-white p-3 border border-[#333] h-32"
              required
            />
          </div>

          <div>
            <label className="block text-sm text-[#A3A3A3] mb-2">Media (Photo or Video)</label>
            <input
              type="file"
              accept="image/*,video/*"
              onChange={(e) => handleMediaUpload(e.target.files[0])}
              className="w-full bg-[#0D0D0D] text-white p-3 border border-[#333]"
              disabled={uploading}
            />
            {uploading && <p className="text-sm text-[#D4AF37] mt-2">Uploading...</p>}
            {formData.media_url && (
              <p className="text-sm text-green-500 mt-2">
                ✓ {formData.media_type === 'photo' ? 'Photo' : 'Video'} uploaded
              </p>
            )}
          </div>

          <div className="flex gap-4">
            <button
              type="submit"
              className="bg-[#D4AF37] text-black px-8 py-3 hover:bg-[#E5C048] transition"
              disabled={uploading}
            >
              {editingBulletin ? 'Update Bulletin' : 'Create Bulletin'}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowForm(false);
                setEditingBulletin(null);
                setFormData({ title: '', description: '', media_url: '', media_type: '' });
              }}
              className="bg-[#333] text-white px-8 py-3 hover:bg-[#444] transition"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Bulletins List */}
      <div className="space-y-4">
        {bulletins.length === 0 ? (
          <div className="text-center py-12 bg-[#1A1A1A] rounded-lg">
            <p className="text-[#737373]">No bulletins yet. Create your first announcement!</p>
          </div>
        ) : (
          bulletins.map((bulletin) => (
            <div key={bulletin.bulletin_id} className="bg-[#1A1A1A] p-6 rounded-lg">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-xl text-white font-bold mb-2">{bulletin.title}</h3>
                  <p className="text-sm text-[#D4AF37]">
                    {formatDate(bulletin.created_at)}
                    {bulletin.updated_at && (
                      <span className="text-[#737373] ml-2">(Updated {formatDate(bulletin.updated_at)})</span>
                    )}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(bulletin)}
                    className="p-2 bg-[#D4AF37] text-black hover:bg-[#E5C048] transition"
                    title="Edit"
                  >
                    <Edit size={18} />
                  </button>
                  <button
                    onClick={() => handleDelete(bulletin.bulletin_id)}
                    className="p-2 bg-red-600 text-white hover:bg-red-700 transition"
                    title="Delete"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>

              {bulletin.media_url && (
                <div className="mb-4">
                  {bulletin.media_type === 'photo' ? (
                    <img 
                      src={bulletin.media_url} 
                      alt={bulletin.title}
                      className="max-h-48 object-contain bg-black"
                    />
                  ) : (
                    <p className="text-sm text-[#737373]">📹 Video attached</p>
                  )}
                </div>
              )}

              <p className="text-white whitespace-pre-wrap">{bulletin.description}</p>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default BulletinsTab;
